import { useDepsChanged, useOptionalApiClient } from './chunk-OLKK33MH.js';
import { useFeatureFlags } from './chunk-ATO7ZCLZ.js';
import * as React2 from 'react';
import { jsx } from 'react/jsx-runtime';
import { GraphQLRequestError } from '@authe/fe-commons/graphql';
import { resolveTransport } from '@authe/fe-commons/transport';
import { createLogger } from '@authe/fe-commons/logging';

var GraphqlClientContext = React2.createContext(null);
function GraphqlProvider({ client, children }) {
  return /* @__PURE__ */ jsx(GraphqlClientContext.Provider, { value: client, children });
}
function useOptionalGraphqlClient() {
  return React2.useContext(GraphqlClientContext);
}
function useAsyncResource(loader, deps) {
  const [data, setData] = React2.useState();
  const [isLoading, setIsLoading] = React2.useState(true);
  const [error, setError] = React2.useState(null);
  const [tick, setTick] = React2.useState(0);
  const loaderRef = React2.useRef(loader);
  loaderRef.current = loader;
  if (useDepsChanged(deps)) {
    setData(void 0);
    setError(null);
    setIsLoading(true);
  }
  React2.useEffect(() => {
    let alive = true;
    setIsLoading(true);
    setError(null);
    loaderRef.current().then((result) => {
      if (alive) {
        setData(result);
        setIsLoading(false);
      }
    }).catch((err) => {
      if (alive) {
        setError(err);
        setIsLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [...deps, tick]);
  const refetch = React2.useCallback(() => setTick((value) => value + 1), []);
  return { data, isLoading, error, refetch, setData };
}
var log = createLogger("ui-core:graphql-query");
function useTransport(operation) {
  const graphqlClient = useOptionalGraphqlClient();
  const flags = useFeatureFlags();
  return resolveTransport(operation, flags, { graphqlConfigured: graphqlClient != null });
}
function useGraphqlQuery(operation, fetchers, options = {}) {
  const client = useOptionalApiClient();
  const graphqlClient = useOptionalGraphqlClient();
  const enabled = options.enabled !== false;
  const transport = useTransport(operation);
  const fetchersRef = React2.useRef(fetchers);
  fetchersRef.current = fetchers;
  const [state, setState] = React2.useState(() => ({
    data: void 0,
    error: void 0,
    isLoading: enabled,
    isFetching: false,
    servedBy: null
  }));
  const [tick, setTick] = React2.useState(0);
  const refetch = React2.useCallback(() => setTick((t) => t + 1), []);
  const extraDeps = options.deps ?? [];
  if (useDepsChanged(extraDeps)) {
    setState({
      data: void 0,
      error: void 0,
      isLoading: enabled,
      isFetching: enabled,
      servedBy: null
    });
  }
  React2.useEffect(() => {
    if (!enabled) return;
    let active = true;
    const ctrl = new AbortController();
    setState((s) => ({ ...s, isFetching: true, isLoading: s.data === void 0, error: void 0 }));
    const run = async () => {
      const { graphql, rest } = fetchersRef.current;
      if (transport === "graphql" && graphqlClient) {
        try {
          return { data: await graphql(graphqlClient, ctrl.signal), servedBy: "graphql" };
        } catch (err) {
          if (!(err instanceof GraphQLRequestError)) throw err;
          if (!rest) throw err;
          log.warn("GraphQL read failed, falling back to REST", {
            operation,
            status: err.status,
            message: err.message
          });
        }
      }
      if (!rest) return null;
      if (!client) {
        throw new Error(
          `useGraphqlQuery(${operation}): the REST path needs an <ApiProvider>, and none is mounted`
        );
      }
      return { data: await rest(client, ctrl.signal), servedBy: "rest" };
    };
    run().then(
      (result) => {
        if (!active) return;
        if (!result) {
          setState((s) => ({ ...s, isLoading: false, isFetching: false }));
          return;
        }
        setState({
          data: result.data,
          error: void 0,
          isLoading: false,
          isFetching: false,
          servedBy: result.servedBy
        });
      },
      (err) => {
        if (!active) return;
        if (ctrl.signal.aborted) {
          setState((s) => ({ ...s, isFetching: false }));
          return;
        }
        setState((s) => ({ ...s, error: err, isLoading: false, isFetching: false }));
      }
    );
    return () => {
      active = false;
      ctrl.abort();
    };
  }, [client, graphqlClient, transport, operation, enabled, tick, ...extraDeps]);
  return { ...state, transport, refetch };
}

export { GraphqlProvider, useAsyncResource, useGraphqlQuery, useOptionalGraphqlClient, useTransport };
//# sourceMappingURL=chunk-GNTFGDJE.js.map
//# sourceMappingURL=chunk-GNTFGDJE.js.map